/**
 * Utility for opening the extension sidepanel and loading a specific client.
 * Handles the full flow: extension check, sidepanel opening, and client loading.
 */

/**
 * Waits for the extension to be ready by pinging it repeatedly.
 * :return: Promise that resolves when extension is ready
 */
const waitForExtension = (): Promise<void> => {
  return new Promise<void>((resolve) => {
    let attempts = 0;
    const maxAttempts = 10;
    let resolved = false;

    const checkExtension = () => {
      if (resolved) return; // Don't continue if already resolved

      console.log("🔍 Checking if extension is ready...");

      const messageHandler = (event: MessageEvent) => {
        if (
          event.origin === window.location.origin &&
          event.data?.type === "EXTENSION_FOUND"
        ) {
          console.log("✅ Extension is ready!");
          resolved = true;
          window.removeEventListener("message", messageHandler);
          resolve();
        }
      };

      window.addEventListener("message", messageHandler);
      window.postMessage({ type: "PING" }, window.location.origin);

      attempts++;
      if (attempts >= maxAttempts) {
        console.warn("⚠️ Extension not responding, proceeding anyway");
        resolved = true;
        window.removeEventListener("message", messageHandler);
        resolve();
      } else {
        setTimeout(() => {
          window.removeEventListener("message", messageHandler);
          checkExtension();
        }, 200);
      }
    };

    checkExtension();
  });
};

/**
 * Opens the extension sidepanel and waits for confirmation.
 * :return: Promise<boolean> - true if sidepanel opened successfully
 */
const openSidepanel = (): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    const timeout = setTimeout(() => {
      console.warn("⚠️ Sidepanel open timeout, proceeding anyway");
      window.removeEventListener("message", messageHandler);
      resolve(false);
    }, 3000);

    const messageHandler = (event: MessageEvent) => {
      if (
        event.origin === window.location.origin &&
        event.data?.type === "SIDEPANEL_OPENED"
      ) {
        console.log("✅ Sidepanel opened successfully");
        clearTimeout(timeout);
        window.removeEventListener("message", messageHandler);
        resolve(true);
      }
    };

    window.addEventListener("message", messageHandler);
    console.log("📤 Sending OPEN_SIDEPANEL message");
    window.postMessage({ type: "OPEN_SIDEPANEL" }, window.location.origin);
  });
};

/**
 * Opens the extension sidepanel and loads a specific client.
 * This is the main function to use when you want to open a client in the extension.
 *
 * :param clientId: The ID of the client to load
 * :param taxYear: Optional tax year to load for the client
 * :return: Promise<boolean> - true if successful
 */
export const openExtensionWithClient = async (
  clientId: string,
  taxYear?: number
): Promise<boolean> => {
  try {
    // Wait for extension to be ready
    await waitForExtension();

    // Open sidepanel and wait for confirmation
    const sidepanelOpened = await openSidepanel();

    if (!sidepanelOpened) {
      console.warn("⚠️ Sidepanel may not have opened properly");
      return false;
    }

    // Send LOAD_CLIENT message with the specific client ID and optional tax year
    console.log(
      "📤 Sending LOAD_CLIENT message with ID:",
      clientId,
      "and taxYear:",
      taxYear
    );
    setTimeout(() => {
      window.postMessage(
        { type: "LOAD_CLIENT", clientId, taxYear },
        window.location.origin
      );
    }, 250);

    return true;
  } catch (error) {
    console.error("Failed to open extension with client:", error);
    return false;
  }
};

/**
 * Notifies the extension to reload onboarding steps.
 * Call this after completing an onboarding-related action (e.g., inviting team members, creating integrations).
 *
 * :return: Promise<boolean> - true if message sent successfully
 */
export const reloadExtensionOnboardingSteps = async (): Promise<boolean> => {
  try {
    // Check if extension is available (without waiting)
    const extensionId = (window as any).smartwizExtensionId;
    if (!extensionId) {
      console.log("Extension not available, skipping onboarding reload");
      return false;
    }

    console.log("📤 Sending RELOAD_ONBOARDING_STEPS message to extension");
    window.postMessage(
      { type: "RELOAD_ONBOARDING_STEPS" },
      window.location.origin
    );

    return true;
  } catch (error) {
    console.error("Failed to reload extension onboarding steps:", error);
    return false;
  }
};

/**
 * Triggers the extension to start the integration connection process.
 * Call this when user reaches step 3 of the integration guide.
 *
 * :param integrationName: The name of the integration to connect
 * :return: Promise<boolean> - true if message sent successfully
 */
export const startExtensionIntegration = async (
  integrationName: string
): Promise<boolean> => {
  try {
    const extensionId = (window as any).smartwizExtensionId;
    if (!extensionId) {
      console.log("Extension not available, cannot start integration");
      return false;
    }

    console.log("📤 Starting integration connection:", integrationName);
    window.postMessage(
      {
        type: "START_INTEGRATION_CONNECTION",
        integrationName,
      },
      window.location.origin
    );

    return true;
  } catch (error) {
    console.error("Failed to start extension integration:", error);
    return false;
  }
};

/**
 * Opens the extension and navigates to the integration onboarding page.
 * This provides a guided onboarding experience directly within the extension.
 *
 * :param integrationName: The name of the integration to onboard (e.g., "Jotform", "Cognito Forms")
 * :return: Promise<boolean> - true if successful
 */
export const openExtensionIntegrationOnboarding = async (
  integrationName: string
): Promise<boolean> => {
  try {
    // Wait for extension to be ready
    await waitForExtension();

    // Open sidepanel and wait for confirmation
    const sidepanelOpened = await openSidepanel();

    if (!sidepanelOpened) {
      console.warn("⚠️ Sidepanel may not have opened properly");
      return false;
    }

    // Send NAVIGATE_TO_INTEGRATION_ONBOARDING message
    console.log(
      "📤 Sending NAVIGATE_TO_INTEGRATION_ONBOARDING message for:",
      integrationName
    );
    setTimeout(() => {
      window.postMessage(
        {
          type: "NAVIGATE_TO_INTEGRATION_ONBOARDING",
          payload: { integrationName },
        },
        window.location.origin
      );
    }, 250);

    return true;
  } catch (error) {
    console.error("Failed to open extension integration onboarding:", error);
    return false;
  }
};

/**
 * Opens the extension to the client creation page.
 *
 * @param showTutorial - Whether to show tutorial overlay (optional future enhancement)
 * @returns Promise<boolean> - true if successful
 */
export const openExtensionToClientCreation = async (
  showTutorial: boolean = false
): Promise<boolean> => {
  try {
    await waitForExtension();
    const sidepanelOpened = await openSidepanel();

    if (!sidepanelOpened) {
      console.warn("⚠️ Sidepanel may not have opened properly");
      return false;
    }

    console.log("📤 Sending NAVIGATE_TO_CLIENT_CREATION message");
    setTimeout(() => {
      window.postMessage(
        {
          type: "NAVIGATE_TO_CLIENT_CREATION",
          payload: { showTutorial },
        },
        window.location.origin
      );
    }, 250);

    return true;
  } catch (error) {
    console.error("Failed to open extension to client creation:", error);
    return false;
  }
};

/**
 * Opens the extension and navigates to a specific settings page.
 *
 * @param page - The settings page route key (e.g., "settings-alpha-groups")
 * @returns Promise<boolean> - true if successful
 */
export const openExtensionToSettingsPage = async (
  page: string
): Promise<boolean> => {
  try {
    await waitForExtension();
    const sidepanelOpened = await openSidepanel();

    if (!sidepanelOpened) {
      console.warn("⚠️ Sidepanel may not have opened properly");
      return false;
    }

    console.log("📤 Sending NAVIGATE_TO_SETTINGS_PAGE message for:", page);
    setTimeout(() => {
      window.postMessage(
        {
          type: "NAVIGATE_TO_SETTINGS_PAGE",
          payload: { page },
        },
        window.location.origin
      );
    }, 250);

    return true;
  } catch (error) {
    console.error("Failed to open extension to settings page:", error);
    return false;
  }
};

/**
 * Opens the extension sidepanel without loading a specific page.
 * Useful for general extension opening on login or when user wants to access the extension.
 *
 * @returns Promise<boolean> - true if successful
 */
export const openExtension = async (): Promise<boolean> => {
  try {
    // Wait for extension to be ready
    await waitForExtension();

    // Open sidepanel and wait for confirmation
    const sidepanelOpened = await openSidepanel();

    if (!sidepanelOpened) {
      console.warn("⚠️ Sidepanel may not have opened properly");
      return false;
    }

    console.log("✅ Extension sidepanel opened successfully");
    return true;
  } catch (error) {
    console.error("Failed to open extension:", error);
    return false;
  }
};
